// App.jsx
import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Card, CardContent } from "./components/ui/card.jsx";
import { db, auth } from "./firebase.js";
import {
  signInWithPopup,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  addDoc,
  collection,
} from "firebase/firestore";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import {
  InstantSearch,
  Stats,
  useInfiniteHits,
  useSearchBox,
  Configure,
  SortBy,
  useInstantSearch,
  RefinementList,
  RangeInput,
  ClearRefinements,
  CurrentRefinements,
  PoweredBy,
} from "react-instantsearch";
import { loadStripe } from "@stripe/stripe-js";
import Feedback from "./Feedback.js";
import TuroEssentials from "./TuroEssentials.js";
import Profile from "./Profile.js";
import Cohosts from "./Cohosts.js";
import { UserProvider, useUser, UserContext } from "./contexts/UserContext.js";
import FAQAccordion from "./FAQAccordion.js";
import Footer from "./Footer.js";
import HostDashboard from "./HostDashboard.js";
import SeoPage from "./SeoPage.js";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";

// SEO page
import TuroRequirementsPage from "./TuroRequirementsPage.jsx";

// ---------- Algolia ----------
const ALGOLIA_APP_ID = "BQQEIR4TFD";
const ALGOLIA_SEARCH_KEY = "141cd1cdb026de5ebf425169694a51a0";
const ALGOLIA_INDEX_NAME = "cars";
const baseSearchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);

// ---------- Limits ----------
const MAX_SEARCHES_PER_DAY = 3;
const WINDOW_DAYS = 1; // 1-day window

ReactDOM.createRoot(document.getElementById("root")).render(
  <HelmetProvider>
    <AppWrapper />
  </HelmetProvider>,
);

function AppWrapper() {
  return (
    <UserProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<App />} />
          <Route
            path="/turo-requirements-to-rent-your-car"
            element={<TuroRequirementsPage />}
          />
          <Route path="/earn-passive-income-with-turo" element={<SeoPage />} />
        </Routes>
      </BrowserRouter>
    </UserProvider>
  );
}

// ---------- Analytics ----------
export async function logUpgradeClick(user, source) {
  if (!user || !source) return;
  const userRef = doc(db, "users", user.uid);
  const log = { source, timestamp: new Date().toISOString() };
  try {
    await updateDoc(userRef, { upgradeClickEvents: arrayUnion(log) });
  } catch (err) {
    console.error("❌ Failed to log upgrade click:", err);
  }
}

// ---------- Search allowance ----------
export async function checkSearchAllowed(user) {
  if (!user) {
    return { ok: false, reason: "no-user" };
  }

  try {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      const isPro = !!data.isPro;

      // handle Firestore Timestamp or millis
      let expiryMs = null;
      if (data.pro_expiry?.toMillis) {
        expiryMs = data.pro_expiry.toMillis();
      } else if (typeof data.pro_expiry === "number") {
        expiryMs = data.pro_expiry;
      }

      const proActive = isPro || (expiryMs && expiryMs > Date.now());
      if (proActive) {
        return { ok: true, reason: "pro-active" };
      }
    }

    // free-tier limit logic
    const limitRef = doc(db, "searchLimits", user.uid);
    const limitSnap = await getDoc(limitRef);

    const now = Date.now();
    const cutoff = now - WINDOW_DAYS * 24 * 60 * 60 * 1000;

    let searches = [];
    if (limitSnap.exists()) {
      const data = limitSnap.data();
      searches = (data.searches || []).filter((ts) => ts > cutoff);
    } else {
      await setDoc(limitRef, {
        searches: [],
        totalSearches: 0,
        dailyCounts: {},
      });
    }

    if (searches.length < MAX_SEARCHES_PER_DAY) {
      return { ok: true, reason: "under-limit" };
    } else {
      return { ok: false, reason: "limit-reached" };
    }
  } catch (err) {
    console.error("checkSearchAllowed error:", err);
    // fallback: don't punish user for our bug
    return { ok: true, reason: "error-fallback" };
  }
}

export async function consumeSearchAttempt(user) {
  if (!user) return false;

  const limitRef = doc(db, "searchLimits", user.uid);
  const limitSnap = await getDoc(limitRef);
  const now = Date.now();
  const todayKey = new Date().toISOString().slice(0, 10);
  const cutoff = now - WINDOW_DAYS * 24 * 60 * 60 * 1000;

  let searches = [];
  let totalSearches = 0;
  let dailyCounts = {};

  if (limitSnap.exists()) {
    const data = limitSnap.data();
    searches = (data.searches || []).filter((ts) => ts > cutoff);
    totalSearches = data.totalSearches || 0;
    dailyCounts = data.dailyCounts || {};
  }

  if (searches.length < MAX_SEARCHES_PER_DAY) {
    searches.push(now);
    totalSearches += 1;
    dailyCounts[todayKey] = (dailyCounts[todayKey] || 0) + 1;

    await setDoc(
      limitRef,
      { searches, totalSearches, dailyCounts },
      { merge: true },
    );
    return true;
  }
  return false;
}

// ---------- Kick search when allowed ----------
function SearchKick({
  authChecked,
  searchReady,
  isPro,
  searchAllowed,
  client,
}) {
  const { refresh } = useInstantSearch();
  const prev = React.useRef(false);

  useEffect(() => {
    const can = authChecked && searchReady && (isPro || searchAllowed);
    const was = prev.current;
    prev.current = can;
    if (!was && can) {
      try {
        const t = client?.transporter;
        t?.responsesCache?.clear?.();
        t?.requestsCache?.clear?.();
        t?.hostsCache?.clear?.();
      } catch {}
      refresh();
    }
  }, [authChecked, searchReady, isPro, searchAllowed, client, refresh]);

  return null;
}

// ======================================================

export function App() {
  const [user, setUser] = useState(null);
  const [searchAllowed, setSearchAllowed] = useState(false);
  const [showPerformanceResults, setShowPerformanceResults] = useState(false);
  const [currentSort, setCurrentSort] = useState(ALGOLIA_INDEX_NAME);
  const [filters, setFilters] = useState("");
  const [isPro, setIsPro] = useState(false);
  const [searchReady, setSearchReady] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsPro(false);
        setSearchAllowed(false);
        setCurrentSort(ALGOLIA_INDEX_NAME);
        setFilters("");
        setSearchReady(true);
        setAuthChecked(true);
        return;
      }

      setUser(u);
      const userRef = doc(db, "users", u.uid);
      let userSnap = await getDoc(userRef);

      const now = new Date().toISOString();
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: u.email || "",
          name: u.displayName || "",
          isPro: false,
          pro_expiry: null,
          createdAt: now,
          lastLogin: now,
        });
        userSnap = await getDoc(userRef);
      } else {
        await setDoc(userRef, { lastLogin: now }, { merge: true });
      }

      const data = userSnap.data();
      const proExpiry = parseProExpiry(data?.pro_expiry);
      const isProNow =
        data?.isPro === true || (proExpiry && proExpiry > new Date());
      setIsPro(!!isProNow);
      const { ok, reason } = await checkSearchAllowed(u);
      const allowed = isProNow ? true : ok;
      setSearchAllowed(allowed);
      setSearchReady(true);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("❌ Sign-in failed:", err);
    }
  };

  async function logSearchQuery(
    user,
    { query, sort, filters, source = "search-bar" },
  ) {
    try {
      await addDoc(collection(db, "searchLogs"), {
        uid: user.uid,
        email: user.email ?? null,
        query: (query || "").trim(),
        sort: sort || "cars",
        filters: filters || "",
        source,
        createdAt: new Date().toISOString(),
        clientTs: Date.now(),
      });
    } catch (err) {
      console.warn("Failed to log search query:", err);
    }
  }

  const handleUpgradeClick = async (source = "unknown") => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    await logUpgradeClick(currentUser, source);
    try {
      const res = await fetch(
        "https://us-central1-diviseed-3b654.cloudfunctions.net/createCheckoutSession",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: currentUser.uid,
            email: currentUser.email,
          }),
        },
      );

      const data = await res.json();
      if (data.sessionId) {
        const stripe = await loadStripe(
          "pk_live_51RpovLB0S87DaMLbM6W76YddxfA1O6eiXdXcUWQErAB9T7NMrbuqSSlVeK1WKPaeEIPrSLHV8VwaP4LgV3kEhpHL00fjMVtIOx",
        );
        stripe.redirectToCheckout({ sessionId: data.sessionId });
      } else {
        console.error("No session ID returned:", data);
      }
    } catch (err) {
      console.error("Upgrade click failed:", err);
    }
  };

  // ---------- Search box with allowance ----------
  function CustomSearchBox({ refreshSearchAllowance, isPro }) {
    const { user: ctxUser } = useUser();
    const { query, refine } = useSearchBox();
    const [input, setInput] = useState(query || "");
    const [allowed, setAllowed] = useState(true);
    const [checking, setChecking] = useState(false);
    const [checkedOnce, setCheckedOnce] = useState(false);

    useEffect(() => {
      (async () => {
        setChecking(true);
        try {
          const ok = await refreshSearchAllowance(ctxUser || null);
          setCheckedOnce(true);
          setAllowed(!!ok);
        } catch (err) {
          setAllowed(false);
        } finally {
          setChecking(false);
        }
      })();
    }, [ctxUser]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!ctxUser) return;
      // Pro users bypass allowance & consumption entirely
      if (isPro) {
        setAllowed(true);
        await logSearchQuery(user, {
          query: input,
          sort: currentSort,
          filters,
          source: "search-bar",
        });
        refine(input);
        return;
      }
      setChecking(true);
      try {
        const { ok, reason } = await checkSearchAllowed(ctxUser); // return shape explained below
        setCheckedOnce(true);

        if (!ok) {
          setAllowed(false); // only disable on a *real* limit denial
          // show a toast with `reason`
          return;
        }

        // Second gate: must successfully *consume* a slot
        const consumed = await consumeSearchAttempt(ctxUser);
        if (!consumed) {
          setAllowed(false);
          // toast('Daily search limit reached.')
          return;
        }
        setAllowed(true);
        await logSearchQuery(user, {
          query: input,
          sort: currentSort,
          filters,
          source: "search-bar",
        });

        refine(input);
      } catch (err) {
        console.error("checkSearchAllowed failed:", err);
        // network/permission/parse error -> do *not* flip allowed false
        // show toast: "Temporary issue checking allowance — try again."
      } finally {
        setChecking(false);
      }
    };

    return (
      <>
        <form
          onSubmit={handleSubmit}
          className="flex border border-gray-300 rounded-md shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-blue-500"
        >
          <input
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              !ctxUser
                ? "Login to search"
                : checking
                  ? "Checking access..."
                  : allowed
                    ? "Search (year make model airport code)..."
                    : "Search limit reached"
            }
            className="w-full px-4 py-2 focus:outline-none"
            disabled={!allowed}
          />
          <button
            type="submit"
            className={`px-4 ${
              allowed
                ? "text-gray-500 hover:text-black"
                : "text-red-500 cursor-not-allowed"
            }`}
            disabled={!allowed}
          >
            {checking ? "..." : "Search"}
          </button>
        </form>

        {checkedOnce && !allowed && (
          <div className="text-sm text-red-600 mt-1">
            Daily search limit reached. Unlock unlimited searches, sorting, and
            other pro features{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleUpgradeClick("search-bar");
              }}
              className="underline text-blue-600 hover:text-blue-800"
            >
              Upgrade to Pro →
            </a>
          </div>
        )}
      </>
    );
  }

  // ---------- Hits list ----------
  function InfiniteHitsWithEmpty({ renderItem }) {
    const { hits, isLastPage, showMore } = useInfiniteHits();
    if (!hits || hits.length === 0) {
      return (
        <p className="w-full text-center text-gray-500 mt-6">
          No results found. Our database is ever-growing based on search demand.
          Check back later!
        </p>
      );
    }
    // Free result cap (soft cap; Algolia already returns hitsPerPage above)
    const visible = hits;
    return (
      <div className="mt-6">
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {visible.map((hit) => (
            <li key={hit.objectID} className="list-none">
              {renderItem(hit)}
            </li>
          ))}
        </ul>
        {!isLastPage && (
          <button
            className="text-center text-blue-600 mt-4 px-3 py-1.5 border rounded hover:bg-blue-50"
            onClick={isPro ? showMore : () => handleUpgradeClick("show-more")}
          >
            {isPro ? "Load More" : "Unlock More Results"}
          </button>
        )}
      </div>
    );
  }

  // ---------- Card ----------
  function CarCard({ hit, isPro = false }) {
    const [editingMV, setEditingMV] = useState(false);
    const [localMarketValue, setLocalMarketValue] = useState(
      Number(hit.market_value) > 0 ? Number(hit.market_value) : 0,
    );

    const derived = useMemo(() => {
      const mv = Number(localMarketValue) || 0;
      const monthly = Number(hit.avg_monthly_revenue) || 0;
      if (mv <= 0 || monthly <= 0) return { roiPct: null, paybackYears: null };
      const annual = monthly * 12;
      const roiPct = (annual / mv) * 100;
      const paybackYears = roiPct > 0 ? 100 / roiPct : null;
      return { roiPct, paybackYears };
    }, [localMarketValue, hit.avg_monthly_revenue]);

    const payoffYears =
      derived.paybackYears != null ? derived.paybackYears.toFixed(1) : null;

    const cleanedLocation = hit.location?.replace(/ US$/, "");

    // Helper: format approx for free tier
    const approx = (n) => {
      if (n == null) return null;
      if (n < 15) return "<15%";
      if (n < 25) return "≈20%";
      if (n < 35) return "≈30%";
      if (n < 45) return "≈40%";
      if (n < 60) return "≈50%";
      return "60%+";
    };

    const exactRoi =
      derived.roiPct != null
        ? derived.roiPct
        : typeof hit.roi === "number"
          ? hit.roi
          : null;

    return (
      <Card className="overflow-visible relative isolate h-full">
        <CardContent className="space-y-2 p-4 pb-10 relative">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold">{hit.make_model}</h2>
              <p className="text-sm text-gray-600">{hit.year}</p>
              {hit.trim && (
                <p className="text-sm text-gray-500 italic">{hit.trim}</p>
              )}
            </div>
            <div className="text-right">
              <div className="relative group w-fit">
                <p className="text-green-600 font-semibold">
                  {hit.avg_daily_price > 0
                    ? `$${hit.avg_daily_price}/day`
                    : "$—"}
                </p>
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex px-2 py-1 text-xs text-white bg-gray-800 rounded shadow z-50 whitespace-nowrap">
                  Average price per day based on recent bookings
                </span>
              </div>
              <div className="relative group w-fit">
                <p className="text-sm text-orange-600">
                  Utilization: {hit.demand ? `${hit.demand}%` : "—"}
                </p>
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex px-2 py-1 text-xs text-white bg-gray-800 rounded shadow z-50 whitespace-nowrap">
                  Average days booked divided by days available based on recent
                  bookings
                </span>
              </div>
            </div>
          </div>

          <div className="relative group w-fit">
            <p className="text-sm">
              <span className="text-gray-500">Monthly Revenue:</span>{" "}
              {hit.avg_monthly_revenue > 0
                ? `$${hit.avg_monthly_revenue}`
                : "$—"}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex px-3 py-2 text-xs text-white bg-gray-800 rounded shadow z-50 whitespace-pre-line max-w-[400px] text-center">
                Average gross revenue made per month
              </span>
            </p>
          </div>

          {/* ROI — precise for Pro, approximate for Free */}
          <div className="relative group w-fit">
            <p className="text-sm">
              <span className="text-gray-500">Yearly ROI:</span>{" "}
              <span className="text-blue-600 font-medium">
                {isPro
                  ? exactRoi != null
                    ? `${exactRoi.toFixed(1)}%`
                    : "—"
                  : exactRoi != null
                    ? approx(exactRoi)
                    : "—"}
              </span>
            </p>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex px-3 py-2 text-xs text-white bg-gray-800 rounded shadow z-50 whitespace-pre-line max-w-[400px] text-center">
              Yearly ROI is estimated earnings per year divided by market value.
              {payoffYears ? ` — pays itself off in ~${payoffYears} years` : ""}
            </span>
          </div>

          {/* Market value editing — Pro only */}
          {isPro && (
            <div className="relative w-fit z-20">
              {!editingMV ? (
                Number(localMarketValue) > 0 ? (
                  <div className="flex items-center gap-2 group">
                    <p className="text-sm text-purple-600 font-medium">
                      Market Value: ${localMarketValue.toLocaleString()}
                    </p>
                    <button
                      type="button"
                      onClick={() => setEditingMV(true)}
                      className="text-xs text-gray-500 hover:text-gray-800 underline"
                      aria-label="Edit market value"
                    >
                      Edit
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex px-2 py-1 text-xs text-white bg-gray-800 rounded shadow z-50 whitespace-nowrap ">
                      Edit local market value to adjust ROI projection
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingMV(true)}
                    className="text-xs text-purple-700 hover:text-purple-900 underline"
                  >
                    Add market value
                  </button>
                )
              ) : (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Market Value:</label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    inputMode="numeric"
                    className="text-sm border rounded px-2 py-1 w-36 bg-white shadow relative z-30"
                    value={localMarketValue || ""}
                    onChange={(e) =>
                      setLocalMarketValue(Math.max(0, Number(e.target.value)))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setEditingMV(false);
                      if (e.key === "Escape") {
                        setLocalMarketValue(
                          Number(hit.market_value) > 0
                            ? Number(hit.market_value)
                            : 0,
                        );
                        setEditingMV(false);
                      }
                    }}
                    onBlur={() => setEditingMV(false)}
                  />
                  <span className="text-xs text-gray-400">USD</span>
                </div>
              )}
            </div>
          )}

          {cleanedLocation && (
            <div className="absolute bottom-2 right-4 text-sm text-gray-500 italic">
              City: {cleanedLocation}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ---------- Filters (Pro-gated sections visible but locked) ----------
  function parseProExpiry(val) {
    if (!val) return null;
    if (typeof val.toMillis === "function") return new Date(val.toMillis()); // Firestore Timestamp
    if (typeof val === "number") return new Date(val); // millis
    if (typeof val === "string") return new Date(val); // ISO
    return null;
  }

  function Section({ title, children }) {
    return (
      <div className="rounded-2xl border p-4 bg-white shadow-sm">
        {title && <h3 className="font-semibold text-sm mb-2">{title}</h3>}
        {children}
      </div>
    );
  }

  function SafeFilterSidebar({ isPro }) {
    return (
      <aside className="w-full md:w-64 shrink-0 space-y-4 md:sticky md:top-[72px] h-fit">
        {/* Hide native number spinners + enforce tabular numerals */}
        <style>{`
        .aisNumInput{
          font-variant-numeric: tabular-nums;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        /* Chrome/Safari */
        input.aisNumInput[type="number"]::-webkit-outer-spin-button,
        input.aisNumInput[type="number"]::-webkit-inner-spin-button{
          -webkit-appearance: none;
          margin: 0;
        }
        input.aisNumInput[type="number"]{
          -webkit-appearance: none;
        }
        /* Firefox */
        input.aisNumInput[type="number"]{
          -moz-appearance: textfield;
        }
      `}</style>

        <Section title="Filters">
          <div className="flex items-center justify-between">
            <CurrentRefinements />
            <ClearRefinements
              translations={{ resetButtonText: "Reset" }}
              classNames={{
                button:
                  "ml-2 px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50 text-gray-700",
              }}
            />
          </div>
        </Section>

        {/* Free filter(s) */}
        <Section title="Year">
          <RangeInput
            attribute="year"
            classNames={rangeInputClasses}
            translations={{ separator: "to", submit: "Apply" }}
          />
        </Section>

        {/* Pro filters (visible + disabled, with clear overlay) */}
        <div className="relative">
          {!isPro && (
            <LockedOverlay onUpgrade={() => handleUpgradeClick("filters")} />
          )}

          <div className={isPro ? "" : "pointer-events-none select-none"}>
            <Section title="Avg Price / Day ($)">
              <RangeInput
                attribute="avg_daily_price"
                classNames={rangeInputClasses}
                translations={{ separator: "to", submit: "Apply" }}
              />
            </Section>

            <Section title="Avg Monthly Revenue ($)">
              <RangeInput
                attribute="avg_monthly_revenue"
                classNames={rangeInputClasses}
                translations={{ separator: "to", submit: "Apply" }}
              />
            </Section>

            <Section title="Utilization (%)">
              <RangeInput
                attribute="demand"
                classNames={rangeInputClasses}
                translations={{ separator: "to", submit: "Apply" }}
              />
            </Section>

            <Section title="ROI (%)">
              <RangeInput
                attribute="roi"
                classNames={rangeInputClasses}
                translations={{ separator: "to", submit: "Apply" }}
              />
            </Section>

            <Section title="Car Market Value ($)">
              <RangeInput
                attribute="market_value"
                classNames={rangeInputClasses}
                translations={{ separator: "to", submit: "Apply" }}
              />
            </Section>
          </div>
        </div>
      </aside>
    );
  }

  const rangeInputClasses = {
    root: "",
    form: "aisRangeForm", // custom grid container (see <style>)
    input:
      "aisRangeInput w-full h-11 px-4 rounded-xl border border-gray-300 bg-white " +
      "text-[16px] leading-[1.4] text-gray-900 placeholder:text-gray-400 " +
      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 " +
      "appearance-none box-border",
    inputMin:
      "aisRangeInput w-full h-11 px-4 rounded-xl border border-gray-300 bg-white " +
      "text-[16px] leading-[1.4] text-gray-900 placeholder:text-gray-400 " +
      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 " +
      "appearance-none box-border rangeMin",
    inputMax:
      "aisRangeInput w-full h-11 px-4 rounded-xl border border-gray-300 bg-white " +
      "text-[16px] leading-[1.4] text-gray-900 placeholder:text-gray-400 " +
      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 " +
      "appearance-none box-border rangeMax",
    separator: "text-gray-500 text-sm rangeSep",
    submit:
      "px-3 py-1 rounded-md text-xs font-medium bg-blue-600 text-white " +
      "space-y-1 hover:bg-blue-700 transition rangeBtn ",
  };

  function LockedOverlay({ onUpgrade }) {
    return (
      <div className="absolute inset-0 z-20 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <div className="rounded-xl bg-white/95 shadow-xl ring-1 ring-black/10 px-4 py-3 text-center">
            <p className="text-sm sm:text-base font-semibold text-gray-900">
              Advanced Filters <br></br>Filter for cars with 2× higher ROI with
              highest demand — instantly.
            </p>
            <button
              onClick={onUpgrade}
              className="mt-2 inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    // keep filters helper in sync in case you add price-asc replica later
    if (currentSort === "cars_price_asc") {
      setFilters("avg_daily_price > 0");
    } else {
      setFilters(undefined);
    }
  }, [currentSort]);

  async function refreshSearchAllowance(userObj) {
    if (!userObj) {
      setSearchAllowed(false);
      setSearchReady(true);
      return false;
    }

    const userRef = doc(db, "users", userObj.uid);
    const userSnap = await getDoc(userRef);
    const data = userSnap.data();

    const proExpiry = parseProExpiry(data.pro_expiry);
    const isProNow =
      data.isPro === true || (proExpiry && proExpiry > new Date());

    if (isProNow) {
      setSearchAllowed(true);
      setSearchReady(true);
      setIsPro(true);
      return true;
    }

    const { ok } = await checkSearchAllowed(userObj);
    setIsPro(false);
    setSearchAllowed(!!ok);
    setSearchReady(true);
    return !!ok;
  }

  const canSearch = authChecked && searchReady && (isPro || searchAllowed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // ---------- Render ----------
  return (
    <UserContext.Provider value={{ user }}>
      <div className="relative w-screen h-screen">
        <InstantSearch
          searchClient={baseSearchClient}
          indexName={ALGOLIA_INDEX_NAME}
        >
          <SearchKick
            authChecked={authChecked}
            searchReady={searchReady}
            isPro={isPro}
            searchAllowed={searchAllowed}
            client={baseSearchClient}
          />

          <Configure
            hitsPerPage={isPro ? 24 : 12}
            {...(currentSort.includes("price_asc")
              ? { filters: "avg_daily_price > 0" }
              : {})}
          />

          <div className="relative flex-1 min-h-screen overflow-auto">
            {/* Auth Bar */}

            <div className="fixed inset-x-0 top-0 z-50 border-b bg-white shadow-sm">
              <div className="relative mx-auto flex h-16 max-w-screen-2xl items-center px-4 sm:px-6 xl:grid xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:gap-4">
                {/* Desktop Left Navigation */}
                <div className="hidden min-w-0 items-center gap-3 xl:flex">
                  {user && <TuroEssentials />}
                  <Feedback />
                </div>

                {/* Center Logo */}
                <h1 className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-xl font-semibold xl:static xl:translate-x-0">
                  Diviseed
                  <span className="hidden 2xl:inline">
                    {" | Turo Market Data & Car ROI Analytics"}
                  </span>
                </h1>

                {/* Desktop Right Navigation */}
                <div className="hidden items-center justify-self-end gap-3 whitespace-nowrap xl:flex">
                  {user ? (
                    <>
                      <Profile />
                      <button
                        type="button"
                        onClick={() => signOut(auth)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSignIn}
                      className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      Login
                    </button>
                  )}
                </div>

                {/* Mobile Hamburger Button */}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="ml-auto rounded-md p-2 text-gray-700 hover:bg-gray-100 xl:hidden"
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-auth-menu"
                >
                  {mobileMenuOpen ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Mobile Dropdown Navigation */}
              {mobileMenuOpen && (
                <nav
                  id="mobile-auth-menu"
                  className="border-t bg-white px-5 py-4 shadow-md xl:hidden"
                >
                  <div className="flex flex-col items-start gap-5">
                    {user && (
                      <>
                        <TuroEssentials />
                        <Cohosts />
                      </>
                    )}

                    <Feedback />

                    {user ? (
                      <>
                        <Profile />
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            signOut(auth);
                          }}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleSignIn();
                        }}
                        className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                      >
                        Login
                      </button>
                    )}
                  </div>
                </nav>
              )}
            </div>

            {/* Header */}
            <div className="p-6 max-w-7xl mx-auto space-y-4 text-center">
              <br />
              <br />
              <h2 className="text-3xl font-bold">
                Find the Most Profitable Cars for Turo in Your Market
              </h2>
              <p className="text-gray-600">
                See actual market demand, daily rates, and utilization by
                vehicle and city before you buy your next car.
                <br />
                <br />
                A better Turo Carculator: Find the best performing rental cars
                in your market — backed by real-world performance data.
                <br />
                <br />
                Perfect for Turo hosts, car investors, and fleet operators
                across the U.S. who want to maximize ROI and stay ahead of the
                curve.
              </p>
              {!user && (
                <button
                  onClick={handleSignIn}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mb-8"
                >
                  Search Your Market
                </button>
              )}
              <div className="mt-2">
                <Link
                  to="/turo-requirements-to-rent-your-car"
                  className="text-sm text-blue-600 hover:underline"
                >
                  New to hosting? Read our 2026 Turo Requirements Guide →
                </Link>
              </div>
            </div>

            <FAQAccordion />
            <HostDashboard />

            {/* Logged-out landing */}
            {!user && (
              <>
                <div className="text-center my-8">
                  <img
                    src="/diviseed-teaser.png"
                    alt="Diviseed teaser"
                    onClick={handleSignIn}
                    className="cursor-pointer w-full max-w-4xl mx-auto rounded-lg shadow-lg"
                  />
                </div>

                <div className="p-6 max-w-7xl mx-auto space-y-4 text-center">
                  <h2 className="text-1xl font-bold">Turo Host Essentials</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* --- CARD 1: Bouncie --- */}
                    <div className="rounded-2xl shadow-lg border border-gray-200 p-4 bg-white">
                      <a
                        href="https://www.amazon.com/dp/B07H8NS5MS?tag=diviseed-20"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="https://m.media-amazon.com/images/I/51adF7mP94L._AC_SX679_.jpg"
                          alt="Bouncie GPS Tracker"
                          className="w-full h-48 object-contain rounded-xl"
                        />
                      </a>
                      <div className="mt-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                          Bouncie GPS Tracker
                        </h2>
                        <p className="text-sm text-gray-500 mb-2">
                          Fleet-ready tracking & driver safety in one
                          plug-and-play device.
                        </p>
                        <ul className="text-sm text-gray-600 list-disc ml-5 space-y-1">
                          <li>Real-time GPS location + trip history</li>
                          <li>Speed, braking, and impact alerts</li>
                          <li>Monitors engine health & diagnostics</li>
                          <li>Easy install — OBD2 port</li>
                        </ul>
                        <a
                          href="https://www.amazon.com/dp/B07H8NS5MS?tag=diviseed-20"
                          target="_blank"
                          className="mt-4 block text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-xl transition"
                        >
                          Buy on Amazon
                        </a>
                      </div>
                    </div>

                    {/* --- CARD 2: Lockbox --- */}
                    <div className="rounded-2xl shadow-lg border border-gray-200 p-4 bg-white">
                      <a
                        href="https://www.amazon.com/dp/B0744PHLRR?tag=diviseed-20"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="https://m.media-amazon.com/images/I/61nAjcWclqL._AC_SX679_.jpg"
                          alt="KeyGuard SL-591-CVR Protective Lockbox Cover"
                          className="w-full h-48 object-contain rounded-xl"
                        />
                      </a>
                      <div className="mt-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                          KeyGuard Lockbox Cover
                        </h2>
                        <p className="text-sm text-gray-500 mb-2">
                          Weatherproof window cover for protecting your SL-591
                          lockbox.
                        </p>
                        <ul className="text-sm text-gray-600 list-disc ml-5 space-y-1">
                          <li>Durable, shatter-resistant polycarbonate</li>
                          <li>UV & weather protection</li>
                          <li>Fits KeyGuard SL-591 lockboxes</li>
                          <li>Secures with 3M adhesive or screws</li>
                          <li>Ideal for outdoor vehicle use</li>
                        </ul>
                        <a
                          href="https://www.amazon.com/dp/B0744PHLRR?tag=diviseed-20"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 block text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-xl transition"
                        >
                          Buy on Amazon
                        </a>
                      </div>
                    </div>

                    {/* --- CARD 3: Ozium --- */}
                    <div className="rounded-2xl shadow-lg border border-gray-200 p-4 bg-white">
                      <a
                        href="https://www.amazon.com/dp/B086T517RH?tag=diviseed-20"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="https://m.media-amazon.com/images/I/614L2+xDSEL._AC_SX679_.jpg"
                          alt="Ozium Air Sanitizer"
                          className="w-full h-48 object-contain rounded-xl"
                        />
                      </a>
                      <div className="mt-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                          Ozium Air Sanitizer Spray
                        </h2>
                        <p className="text-sm text-gray-500 mb-2">
                          Original scent spray that eliminates tough odors in
                          cars and enclosed spaces.
                        </p>
                        <ul className="text-sm text-gray-600 list-disc ml-5 space-y-1">
                          <li>Trusted odor eliminator used by pros</li>
                          <li>Neutralizes smoke & bacteria</li>
                          <li>Compact 3.5 oz can</li>
                          <li>Great post-rental or road trip</li>
                          <li>Strong, clean original scent</li>
                        </ul>
                        <a
                          href="https://www.amazon.com/dp/B086T517RH?tag=diviseed-20"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 block text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-xl transition"
                        >
                          Buy on Amazon
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Search Toolbar */}
            <div className="px-4 md:px-6 xl:px-12 2xl:px-20 max-w-screen-2xl mx-auto space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <div className="w-full flex flex-col sm:flex-row sm:items-center gap-4">
                  {user && (
                    <div className="w-full sm:flex-1 sm:max-w-xl">
                      <CustomSearchBox
                        refreshSearchAllowance={refreshSearchAllowance}
                        isPro={isPro}
                      />
                    </div>
                  )}

                  {/* Sort gating UI (Pro only) */}
                  {user && isPro ? (
                    <div className="w-full sm:w-auto  gap-4">
                      <SortBy
                        defaultRefinement="cars"
                        items={[
                          { label: "Sort: Default", value: "cars" },
                          {
                            label: "Revenue (High to Low)",
                            value: "cars_annual_revenue_desc",
                          },
                          {
                            label: "ROI (High to Low)",
                            value: "cars_roi_desc",
                          },
                          {
                            label: "Price (High to Low )",
                            value: "cars_price_desc",
                          },
                          {
                            label: "Year (Newest First)",
                            value: "cars_year_desc",
                          },
                        ]}
                        onChange={({ currentRefinement }) => {
                          setCurrentSort(currentRefinement);
                        }}
                      />
                    </div>
                  ) : (
                    user && (
                      <>
                        <div className="w-full sm:w-auto  gap-4">
                          <SortBy
                            defaultRefinement="cars"
                            items={[
                              { label: "Sort: Default", value: "cars" },
                              {
                                label: "Price (High to Low )",
                                value: "cars_price_desc",
                              },
                              {
                                label: "Year (Newest First)",
                                value: "cars_year_desc",
                              },
                            ]}
                            onChange={({ currentRefinement }) => {
                              setCurrentSort(currentRefinement);
                            }}
                          />
                        </div>
                        <div className="w-full sm:w-auto">
                          <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-3 text-sm shadow-sm w-full sm:w-[280px]">
                            <div className="flex flex-col">
                              <h3 className="text-sm font-semibold text-gray-800 mb-0.5">
                                Unlock Pro Sorting
                              </h3>
                              <p className="text-xs text-gray-600">
                                Sort listings by highest revenue and ROI for
                                smarter decisions.
                              </p>
                              <button
                                onClick={() => handleUpgradeClick("sort")}
                                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-medium rounded-lg transition self-start"
                              >
                                Upgrade to Pro
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  )}
                </div>

                {showPerformanceResults && (
                  <Stats
                    className="text-sm text-gray-600 whitespace-nowrap"
                    translations={{
                      stats(nbHits, processingTimeMS) {
                        return `${nbHits.toLocaleString()} results found in ${processingTimeMS}ms`;
                      },
                    }}
                  />
                )}
              </div>

              {/* Results + Sidebar */}
              <div className="grid grid-cols-1 md:grid-cols-[16rem_minmax(0,1fr)] gap-6 mt-4">
                <SafeFilterSidebar isPro={isPro} />

                <div className="relative">
                  {!canSearch && (
                    <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-gray-700 mb-2">
                          Log in or upgrade to Pro to view results.
                        </p>
                        {!user && (
                          <button
                            onClick={handleSignIn}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded"
                          >
                            Login
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <InfiniteHitsWithEmpty
                    renderItem={(hit) => <CarCard hit={hit} isPro={isPro} />}
                  />
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </InstantSearch>
      </div>
    </UserContext.Provider>
  );
}

export default AppWrapper;
